import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Search } from 'lucide-react';
import { startOfDay } from 'date-fns';
import FormFieldComponent from '@/components/FormFieldComponent';
import { FormFieldSelect } from '@/components/FormFieldSelect';
import FormFieldSearch from '@/components/FormFieldSearch';
import FormFieldCalendar from '@/components/FormFieldCalendar';
import CreateUpdateClientModal from '@pages/caseManagement/CreateUpdateClientModal';
import { CreateCourtModal } from '@pages/courts/CreateCourtModal';
import { clientSearch, clientContactSearch, courtSearch } from '@/services/Network';
import { useClientById } from '@/services/clients';
import {
  useDDICaseSubtypes,
  useDDICaseTypes,
  useDDISubtypes,
  useDDITypes,
} from '@/services/dueDiligenceService';
import type { CourtGetAllParams } from '@/types/court.types';
import type {
  ClientGetAllParams,
  ClientResponse,
  ContactGetAllParams,
  ContactResponseType,
} from '@/types/client.types';
import type { DdiCreateFormValues } from './ddiWorkOrderCreate.schema';
import { clearSearchDetailsFields, estimateDueDateFromRushOrType } from './ddiWorkOrderCreate.schema';

const SearchIcon = {
  Icon: Search,
  iconProps: { className: 'text-[#969696] h-5 w-5' },
};

const disableFutureDates = (date: Date) => startOfDay(date) > startOfDay(new Date());

export const DDIWorkOrderCreateStep1 = ({ isEditMode = false }: { isEditMode?: boolean }) => {
  const { control, setValue, getValues, clearErrors, watch } = useFormContext<DdiCreateFormValues>();
  const [clientId, setClientId] = useState<string | undefined>(getValues('clientId') ?? undefined);
  const [newClient, setNewClient] = useState<ClientResponse | undefined>(undefined);
  const [refreshKey, setRefreshKey] = useState(0);
  const [addNewClient, setAddNewClient] = useState(false);
  const [addNewContactFor, setAddNewContactFor] = useState<ContactResponseType | null>(null);
  const [addNewCourt, setAddNewCourt] = useState(false);
  const [courtRefreshKey, setCourtRefreshKey] = useState(0);

  const ddiTypeId = watch('ddiTypeId');
  const caseType = watch('caseType');
  const dueDateTouched = watch('dueDateTouched');

  const { data: ddiTypes = [] } = useDDITypes();
  const {
    data: subtypes = [],
    isLoading: subtypesLoading,
    isFetching: subtypesFetching,
    isFetched: subtypesFetched,
  } = useDDISubtypes(ddiTypeId ?? undefined);
  const { data: caseTypes = [] } = useDDICaseTypes();
  const { data: caseSubtypes = [] } = useDDICaseSubtypes(caseType ? caseType : undefined);

  const ddiTypeOptions = useMemo(
    () => ddiTypes.map((t) => ({ label: t.name, value: t.name })),
    [ddiTypes]
  );
  const subtypeOptions = useMemo(
    () => subtypes.map((t) => ({ label: t.name, value: t.name })),
    [subtypes]
  );
  const caseTypeOptions = useMemo(
    () => caseTypes.map((t) => ({ label: t.name, value: t.name })),
    [caseTypes]
  );
  const caseSubtypeOptions = useMemo(
    () => caseSubtypes.map((t) => ({ label: t.name, value: t.name })),
    [caseSubtypes]
  );

  const subtypesLookupPending = !!ddiTypeId && (subtypesLoading || subtypesFetching || !subtypesFetched);

  useEffect(() => {
    setValue('subtypesLookupPending', subtypesLookupPending);
  }, [subtypesLookupPending, setValue]);

  useEffect(() => {
    if (!isEditMode) return;
    const typeName = getValues('ddiType');
    if (!typeName || ddiTypes.length === 0) return;
    const selected = ddiTypes.find((t) => t.name === typeName);
    if (selected?.id && getValues('ddiTypeId') !== selected.id) {
      setValue('ddiTypeId', selected.id);
    }
  }, [isEditMode, ddiTypes, getValues, setValue]);

  useEffect(() => {
    if (!isEditMode) return;
    const subtypeName = getValues('ddiSubtype');
    if (!subtypeName || subtypes.length === 0) return;
    const selected = subtypes.find((t) => t.name === subtypeName);
    if (selected?.id && getValues('ddiSubtypeId') !== selected.id) {
      setValue('ddiSubtypeId', selected.id);
    }
  }, [isEditMode, subtypes, getValues, setValue]);

  useEffect(() => {
    if (!ddiTypeId) {
      setValue('subtypeRequired', false);
      // In edit mode ddiTypeId is resolved asynchronously after prefill — do not wipe subtype.
      if (!isEditMode) {
        setValue('ddiSubtype', null);
        setValue('ddiSubtypeId', null);
      }
      return;
    }
    // Do not update subtypeRequired until the lookup has settled — avoids Next while empty.
    if (subtypesLookupPending) return;
    setValue('subtypeRequired', subtypes.length > 0);
    if (subtypes.length === 0 && !isEditMode) {
      setValue('ddiSubtype', null);
      setValue('ddiSubtypeId', null);
    }
  }, [subtypes, ddiTypeId, subtypesLookupPending, setValue, isEditMode]);

  const { data: client } = useClientById(clientId, { refreshKey });

  useEffect(() => {
    if (client) {
      setValue('clientName', client.clientName, { shouldValidate: true });
      setValue('clientCode', client.code, { shouldValidate: true });
    }
  }, [client, setValue]);

  useEffect(() => {
    if (newClient) {
      resetClientDependents();
      setClientId(newClient.clientId);
      setValue('clientId', newClient.clientId, { shouldValidate: true });
      setValue('clientCode', newClient.code, { shouldValidate: true });
      setValue('clientName', newClient.clientName);
      setRefreshKey((prev) => prev + 1);
    }
  }, [newClient]);

  const resetClientDependents = () => {
    setValue('clientName', null);
    setValue('clientCode', '');
    setValue('contactId', '');
    setValue('contactEmail', null);
    setValue('lawyerId', null);
    clearErrors(['clientName', 'clientCode', 'contactId', 'contactEmail', 'lawyerId']);
  };

  return (
    <div className="flex flex-col w-full mt-11 gap-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldComponent
          control={control}
          name="serviceType"
          label="Service Type"
          placeholder="Due Diligence"
          disabled
        />
        <FormFieldSelect
          name="ddiType"
          options={ddiTypeOptions}
          placeholder="Select DDI Type"
          label="DDI Type"
          required
          disabled={isEditMode}
          onChange={(value) => {
            if (isEditMode) return;
            const selected = ddiTypes.find((t) => t.name === value);
            setValue('ddiTypeId', selected?.id ?? null);
            setValue('ddiSubtype', null);
            setValue('ddiSubtypeId', null);
            clearSearchDetailsFields(setValue);
            if (!dueDateTouched && value) {
              const sameDayRush = !!getValues('sameDayRush');
              const nextDayRush = !!getValues('nextDayRush');
              setValue(
                'dueDate',
                estimateDueDateFromRushOrType(value, { sameDayRush, nextDayRush })
              );
            }
          }}
        />
      </div>

      {subtypeOptions.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
          <FormFieldSelect
            name="ddiSubtype"
            options={subtypeOptions}
            placeholder="Select DDI Subtype"
            label="DDI Subtype"
            required
            onChange={(value) => {
              const selected = subtypes.find((t) => t.name === value);
              setValue('ddiSubtypeId', selected?.id ?? null);
              setValue('reportId', null);
            }}
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldCalendar
          control={control}
          name="entryDate"
          label="Entry Date"
          placeholder="Entry Date"
          required
          disabled={isEditMode}
          disabledAt={disableFutureDates}
        />
        <FormFieldComponent
          control={control}
          name="fileNumber"
          label="File Number"
          placeholder="File Number"
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldSearch<ClientGetAllParams>
          name="clientId"
          key={refreshKey}
          required
          label="Client Code"
          placeholder="Search Client Code"
          props={SearchIcon}
          searchFieldName="code"
          api={(params, options) => {
            const updatedParams = { ...params, pageSize: 100, ...(newClient && { code: newClient.code }) };
            return clientSearch(updatedParams, options, refreshKey);
          }}
          onChange={(value, option) => {
            if (clientId && clientId === value) return;
            resetClientDependents();
            setClientId(value);
            const client = option as { code?: string; clientName?: string; data?: ClientResponse } | undefined;
            const code = client?.data?.code ?? client?.code;
            if (code) setValue('clientCode', code, { shouldValidate: true });
          }}
          addNewOption={{
            label: 'Add New Client',
            onNewOptionClick: () => setAddNewClient(true),
          }}
          onSearchChange={() => {
            setNewClient(undefined);
            setClientId(undefined);
            resetClientDependents();
          }}
        />
        <FormFieldComponent
          control={control}
          name="clientName"
          label="Client Name"
          placeholder="Client Name"
          required
          disabled
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldSearch<ContactGetAllParams>
          name="contactId"
          placeholder="Search Contact's Name"
          label="Contact's Name"
          props={SearchIcon}
          required
          disabled={!clientId}
          api={(params, options) => {
            const updatedParams = { ...params, pageSize: 100, clientId };
            return clientContactSearch(updatedParams, options, undefined, refreshKey);
          }}
          onChange={(_value, option) => {
            const contact = option as { email?: string; contactEmail?: string } | undefined;
            if (contact) {
              setValue('contactEmail', contact.email ?? contact.contactEmail ?? null);
            }
          }}
          {...(clientId && {
            addNewOption: {
              label: 'Add New Client Contact',
              onNewOptionClick: () => {
                setAddNewContactFor('STAFF' as ContactResponseType);
              },
            },
          })}
        />
        <FormFieldComponent
          control={control}
          name="contactEmail"
          label="Contact's Email Address"
          placeholder="Contact Email"
          required
          disabled
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldSearch<ContactGetAllParams>
          name="lawyerId"
          placeholder="Search Lawyer"
          label="Lawyer"
          props={SearchIcon}
          disabled={!clientId}
          api={(params, options) => {
            const updatedParams = { ...params, pageSize: 100, clientId };
            return clientContactSearch(updatedParams, options, 'LAWYER' as ContactResponseType, refreshKey);
          }}
          {...(clientId && {
            addNewOption: {
              label: 'Add New Client Contact',
              onNewOptionClick: () => {
                setAddNewContactFor('LAWYER' as ContactResponseType);
              },
            },
          })}
        />
        <FormFieldSearch<CourtGetAllParams>
          name="courtName"
          label="Court Name"
          placeholder="Search Court"
          props={SearchIcon}
          addNewOption={{
            label: 'Add New Court',
            onNewOptionClick: () => setAddNewCourt(true),
          }}
          api={(params, options) => {
            const result = courtSearch(params, options, courtRefreshKey);
            return {
              ...result,
              values: (result.values ?? []).map((v) => ({
                label: v.label,
                value: v.label.includes(' - ') ? v.label.split(' - ').slice(1).join(' - ') : v.label,
              })),
            };
          }}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldSelect
          name="caseType"
          options={caseTypeOptions}
          placeholder="Select Case Type"
          label="Case Type"
          required
          onChange={() => {
            setValue('caseSubType', '');
            clearErrors('caseSubType');
          }}
        />
        <FormFieldSelect
          name="caseSubType"
          options={caseSubtypeOptions}
          placeholder="Select Case Sub-Type"
          label="Case Sub-Type"
          required
          disabled={!caseType}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-black">
        <FormFieldComponent
          control={control}
          name="caseNumber"
          label="Case Number"
          placeholder="Case Number"
          required
          disabled={isEditMode}
        />
        <FormFieldComponent
          control={control}
          name="caseName"
          label="Case Name"
          placeholder="Case Name"
          required
        />
      </div>

      {addNewClient && (
        <CreateUpdateClientModal
          onClose={() => setAddNewClient(false)}
          onUpdate={(client) => setNewClient(client)}
          operation="CREATE_CLIENT"
        />
      )}
      {addNewContactFor && (
        <CreateUpdateClientModal
          onClose={() => setAddNewContactFor(null)}
          onUpdate={(updatedClient) => {
            const newContact = updatedClient.contacts?.[0];
            if (newContact) {
              if (addNewContactFor === ('STAFF' as ContactResponseType)) {
                setValue('contactId', newContact.contactId ?? '');
                setValue('contactEmail', (newContact as { email?: string }).email ?? null);
              } else if (addNewContactFor === ('LAWYER' as ContactResponseType)) {
                setValue('lawyerId', newContact.contactId ?? null);
              }
            }
            setRefreshKey((prev) => prev + 1);
          }}
          operation="UPDATE_CONTACT"
          clientId={clientId}
        />
      )}
      {addNewCourt && (
        <CreateCourtModal
          onClose={() => setAddNewCourt(false)}
          setUpdatedCourt={() => setCourtRefreshKey((prev) => prev + 1)}
        />
      )}
    </div>
  );
};
